import { StyleSheet } from "react-native";
import { colors, spacing, textStyle, backButtonStyle } from "../styles";

export const COLUMNS = 4;
export const COLUMN_GAP = 24;
export const ROW_GAP = 32;
/** Horizontal/vertical inset for the gallery page (and the shared-with-you page,
 *  so the two line up). */
export const PAGE_PADDING = 24;

// Fixed card height shared by the library cards and the "Shared with you" card
// so they line up in the grid (the Figma library card is 212 tall).
export const CARD_HEIGHT = 212;

export const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.galleryBg,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: PAGE_PADDING,
    paddingVertical: PAGE_PADDING,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  title: {
    fontSize: 28,
    ...textStyle.headingMedium,
    color: colors.galleryOnSurface,
  },
  backBtn: backButtonStyle.container,
  backIcon: backButtonStyle.icon,
  closeBtnClosing: {
    opacity: 0.5,
  },

  // Line heights keep the two-line title within the 48px back-button height.
  deleteTitle: {
    lineHeight: 30,
  },
  deleteSubtitle: {
    fontSize: 14,
    lineHeight: 16,
    color: colors.textMuted,
    ...textStyle.body,
  },
  deleteModeBtn: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.btnSecondary,
    alignItems: "center",
    justifyContent: "center",
  },
  deleteModeBtnIcon: {
    width: 24,
    height: 24,
    tintColor: colors.white,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  cancelPill: {
    height: 48,
    borderRadius: 24,
    paddingHorizontal: 20,
    backgroundColor: colors.btnSecondary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  cancelPillText: {
    fontSize: 16,
    color: colors.white,
    ...textStyle.body,
  },
  deletePill: {
    height: 48,
    borderRadius: 24,
    paddingHorizontal: 20,
    backgroundColor: colors.danger,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  deletePillDisabled: {
    opacity: 0.4,
  },
  deletePillIcon: {
    width: 20,
    height: 20,
    tintColor: colors.white,
  },
  deletePillText: {
    fontSize: 16,
    color: colors.white,
    ...textStyle.body,
  },
  cardActionsDisabled: {
    opacity: 0.4,
  },
  cardCheckbox: {
    position: "absolute",
    top: 16,
    right: 16,
  },
  checkbox: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxUnchecked: {
    borderWidth: 2,
    borderColor: colors.checkboxOutline,
  },
  checkboxChecked: {
    backgroundColor: colors.galleryBtnBg,
  },
  // Checkmark drawn from two borders rotated 45° (no asset).
  checkMark: {
    width: 7,
    height: 12,
    marginTop: -2,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    borderColor: colors.text,
    transform: [{ rotate: "45deg" }],
  },
  cardDisabled: {
    opacity: 0.4,
  },

  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
    paddingHorizontal: PAGE_PADDING,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: COLUMN_GAP,
    rowGap: ROW_GAP,
  },

  card: {
    height: CARD_HEIGHT,
    backgroundColor: colors.galleryCard,
    borderRadius: 36,
    padding: 8,
    flexDirection: "row",
    alignItems: "stretch",
    gap: 14,
  },
  cardImage: {
    borderRadius: 24,
    overflow: "hidden",
    backgroundColor: colors.galleryCardCoverBg,
  },
  coverImage: {
    width: "100%",
    height: "100%",
  },
  cardContent: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: 48,
    paddingBottom: 8,
    paddingRight: 4,
    gap: 8,
  },
  cardTitleArea: {
    flex: 1,
    justifyContent: "center",
    paddingRight: 10,
  },
  cardTitle: {
    fontSize: 18,
    lineHeight: 22,
    color: colors.galleryOnSurface,
    ...textStyle.body,
  },
  cardActions: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  editPillBtn: {
    flex: 1,
    height: 40,
    borderRadius: 9999,
    backgroundColor: colors.galleryBtnBg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  editPillIcon: {
    width: 24,
    height: 24,
    tintColor: colors.primary,
  },
  editPillText: {
    fontSize: 16,
    color: colors.primary,
    ...textStyle.body,
  },

  initialLoader: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  initialLoaderText: {
    color: colors.textMuted,
    marginTop: spacing.md,
    fontSize: 15,
    ...textStyle.body,
  },
  emptyText: {
    color: colors.textMuted,
    fontSize: 14,
    fontStyle: "italic",
    ...textStyle.body,
  },
  loadingMore: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    paddingVertical: 16,
  },
  loadingMoreText: {
    color: colors.galleryOnSurface,
    fontSize: 16,
    ...textStyle.body,
  },

  // "Shared with you" card (Figma): surface-container card, p-8, cover fills the
  // top, footer row with title + up to two overlapping sender avatars.
  sharedCard: {
    borderRadius: 36,
    backgroundColor: colors.galleryCard, // #262842 surface-container
    padding: 8,
    overflow: "hidden",
    flexShrink: 0,
  },
  sharedCardCover: {
    flex: 1,
    borderRadius: 28,
    overflow: "hidden",
    backgroundColor: colors.galleryCardCoverBg,
  },
  sharedCardCoverImage: {
    width: "100%",
    height: "100%",
  },
  sharedCardCoverPlaceholder: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  sharedCardCoverEmoji: {
    fontSize: 40,
  },
  sharedCardFooter: {
    height: 62,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    gap: 12,
  },
  sharedCardTitle: {
    flex: 1,
    color: "#F7F4EE",
    fontSize: 16,
    lineHeight: 21.33,
    ...textStyle.headingMedium,
  },
  sharedCardAvatars: {
    flexDirection: "row",
    alignItems: "center",
  },
  sharedCardAvatarOverlap: {
    marginRight: -16,
  },
});
